import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ExternalLink, Clock, Target, Book, Users, Check, Star, ArrowRight } from 'lucide-react';
import PageSEO from '../components/SEO/PageSEO';

const CoursParticuliersPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Schéma JSON-LD pour le service
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Cours Particuliers de SES",
    "description": "Cours particuliers de Sciences Économiques et Sociales personnalisés en visioconférence pour les élèves de Première et Terminale.",
    "provider": {
      "@type": "Organization",
      "name": "Comprendre les SES",
      "image": "https://cours-ses.fr/Logo SES.png"
    },
    "offers": {
      "@type": "Offer",
      "price": "25.00",
      "priceCurrency": "EUR",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "25.00",
        "priceCurrency": "EUR",
        "unitText": "HOUR"
      }
    },
    "serviceType": "Cours particuliers en ligne",
    "areaServed": "France",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://www.wooskill.com/fr/catalog/17929",
      "servicePhone": "+33600000000",
      "serviceSmsNumber": "+33600000000"
    },
    "termsOfService": "https://cours-ses.fr/cgv"
  };

  // FAQ pour le schéma structuré
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Comment se déroulent les cours en visio ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Les cours se déroulent sur la plateforme Wooskill, qui offre tous les outils nécessaires pour un apprentissage efficace : tableau blanc interactif, partage d'écran, chat, etc. Vous recevez un lien de connexion avant chaque séance."
        }
      },
      {
        "@type": "Question",
        "name": "Puis-je annuler ou reporter un cours ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, vous pouvez annuler ou reporter un cours jusqu'à 24h avant le début de la séance. Dans ce cas, vous serez intégralement remboursé ou pourrez choisir un autre créneau."
        }
      },
      {
        "@type": "Question",
        "name": "Comment sont personnalisés les cours ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Avant chaque première séance, nous évaluons votre niveau et vos besoins spécifiques. Le contenu est ensuite adapté pour répondre à vos objectifs, que ce soit pour renforcer certaines notions ou préparer des examens."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageSEO
        title="Cours Particuliers SES en Visio | Première et Terminale"
        description="Cours particuliers de SES personnalisés en visioconférence pour les élèves de Première et Terminale. Progressez à votre rythme avec Brandon & Marion !"
        path="/cours-particuliers"
        image="https://cours-ses.fr/Logo SES.png"
        schema={schema}
      />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#9a6ebd]/20 via-[#9a6ebd]/10 to-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-[#323c5f] mb-4">
                Cours Particuliers de SES
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Des cours en visioconférence personnalisés pour les élèves de Première et Terminale.
                Progressez à votre rythme avec un accompagnement sur mesure.
              </p>
              <a
                href="https://www.wooskill.com/fr/catalog/17929"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-[#9a6ebd] text-white px-8 py-4 rounded-lg hover:bg-[#8a5dad] transition-colors text-lg font-semibold"
              >
                Voir nos offres
                <ArrowRight className="ml-2" size={20} />
              </a>
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* Process Section */}
        <section className="py-16 bg-gradient-to-br from-white via-[#9a6ebd]/5 to-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBtLTI4IDBhMjggMjggMCAxIDAgNTYgMCAyOCAyOCAwIDEgMC01NiAwIiBzdHJva2U9IiM5YTZlYmQiIHN0cm9rZS13aWR0aD0iLjUiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-50"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-[#9a6ebd]/20 mb-12">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-[#9a6ebd] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Star className="text-white w-10 h-10 md:w-12 md:h-12" />
                </div>
                <div className="text-center md:text-left">
                  <h2 className="text-2xl md:text-3xl font-bold text-[#323c5f] mb-2">
                    Comment ça marche ?
                  </h2>
                  <p className="text-gray-600 md:text-lg">
                    Tous nos cours sont gérés via la plateforme Wooskill, un outil professionnel 
                    spécialisé dans les cours en ligne. Un processus simple en 3 étapes :
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 relative">
              <div className="bg-white p-6 rounded-xl shadow-lg border border-[#ceace8] relative transform transition-all duration-300 hover:-translate-y-2">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <Clock className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-[#323c5f] mb-2">
                  1. Réservez votre créneau
                </h3>
                <p className="text-gray-600">
                  Choisissez la date et l'heure qui vous conviennent sur Wooskill. 
                  Indiquez vos besoins et le chapitre à travailler.
                </p>
                {/* Flèche vers la droite */}
                <div className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                  <div className="w-8 h-8 bg-[#9a6ebd] rounded-full flex items-center justify-center">
                    <ArrowRight className="text-white" size={20} />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border border-[#ceace8] relative transform transition-all duration-300 hover:-translate-y-2">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <Target className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-[#323c5f] mb-2">
                  2. Suivez votre cours
                </h3>
                <p className="text-gray-600">
                  Rejoignez la visioconférence en un clic depuis votre espace Wooskill. 
                  On adapte le cours selon vos objectifs.
                </p>
                {/* Flèche vers la droite */}
                <div className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 z-10">
                  <div className="w-8 h-8 bg-[#9a6ebd] rounded-full flex items-center justify-center">
                    <ArrowRight className="text-white" size={20} />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border border-[#ceace8] transform transition-all duration-300 hover:-translate-y-2">
                <div className="w-12 h-12 bg-[#9a6ebd] rounded-xl flex items-center justify-center mb-4">
                  <Book className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-[#323c5f] mb-2">
                  3. Recevez vos supports
                </h3>
                <p className="text-gray-600">
                  Après le cours, vous recevez une fiche de synthèse et des exercices 
                  pour vous entraîner via la plateforme.
                </p>
              </div>
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* Pricing Section */}
        <section id="offres" className="py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#9a6ebd]/10 to-white"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBIMHoiLz48cGF0aCBkPSJNMjAgMjBtLTE4IDBhMTggMTggMCAxIDAgMzYgMCAxOCAxOCAwIDEgMC0zNiAwIiBzdHJva2U9IiM5YTZlYmQiIHN0cm9rZS13aWR0aD0iLjUiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Terminale */}
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-[#323c5f] text-center mb-8">
                Cours Particuliers Terminale
              </h2>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* 1h */}
                <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#323c5f]">Cours 1h</h3>
                    <div className="text-2xl font-bold text-[#9a6ebd]">25€</div>
                  </div>
                  <ul className="space-y-3 flex-grow">
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Cours individuel en visio
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Support de cours personnalisé
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Exercices corrigés
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Suivi des progrès
                    </li>
                  </ul>
                  <a
                    href="https://www.wooskill.com/fr/checkout-ws/dbc17174-6211-4ea0-9ad9-b9fdeccbfa6d"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full bg-white text-[#9a6ebd] border-2 border-[#9a6ebd] px-6 py-3 rounded-lg hover:bg-[#9a6ebd]/5 transition-colors text-center font-medium"
                  >
                    Réserver
                    <ExternalLink className="inline-block ml-2" size={16} />
                  </a>
                </div>

                {/* 2h */}
                <div className="bg-white rounded-xl shadow-lg p-8 relative border-2 border-[#9a6ebd] flex flex-col">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#9a6ebd] text-white px-4 py-1 rounded-full text-sm font-medium">
                      Recommandé
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#323c5f]">Cours 2h</h3>
                    <div className="text-2xl font-bold text-[#9a6ebd]">40€</div>
                  </div>
                  <ul className="space-y-3 flex-grow">
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Cours individuel en visio
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Support de cours personnalisé
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Exercices corrigés
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Suivi des progrès
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Plus de temps pour approfondir
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Meilleur rapport qualité/prix
                    </li>
                  </ul>
                  <a
                    href="https://www.wooskill.com/fr/checkout-ws/bed60e6e-2e3f-4353-9577-42b2c46936c5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full bg-[#9a6ebd] text-white px-6 py-3 rounded-lg hover:bg-[#8a5dad] transition-colors text-center font-medium"
                  >
                    Réserver
                    <ExternalLink className="inline-block ml-2" size={16} />
                  </a>
                </div>
              </div>
            </div>

            {/* Première */}
            <div>
              <h2 className="text-2xl font-bold text-[#323c5f] text-center mb-8">
                Cours Particuliers Première
              </h2>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* 1h */}
                <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#323c5f]">Cours 1h</h3>
                    <div className="text-2xl font-bold text-[#9a6ebd]">25€</div>
                  </div>
                  <ul className="space-y-3 flex-grow">
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Cours individuel en visio
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Support de cours personnalisé
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Exercices corrigés
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Suivi des progrès
                    </li>
                  </ul>
                  <a
                    href="https://www.wooskill.com/fr/checkout-ws/1495cccd-0207-4e8f-9fb8-6dc461e6cdda"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full bg-white text-[#9a6ebd] border-2 border-[#9a6ebd] px-6 py-3 rounded-lg hover:bg-[#9a6ebd]/5 transition-colors text-center font-medium"
                  >
                    Réserver
                    <ExternalLink className="inline-block ml-2" size={16} />
                  </a>
                </div>

                {/* 2h */}
                <div className="bg-white rounded-xl shadow-lg p-8 relative border-2 border-[#9a6ebd] flex flex-col">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#9a6ebd] text-white px-4 py-1 rounded-full text-sm font-medium">
                      Recommandé
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#323c5f]">Cours 2h</h3>
                    <div className="text-2xl font-bold text-[#9a6ebd]">40€</div>
                  </div>
                  <ul className="space-y-3 flex-grow">
                    <li className="flex items- center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Cours individuel en visio
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Support de cours personnalisé
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Exercices corrigés
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Suivi des progrès
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Plus de temps pour approfondir
                    </li>
                    <li className="flex items-center text-gray-600">
                      <Check className="text-[#9a6ebd] mr-2 flex-shrink-0" size={16} />
                      Meilleur rapport qualité/prix
                    </li>
                  </ul>
                  <a
                    href="https://www.wooskill.com/fr/checkout-ws/04a9f39c-dc8b-4cc4-9006-9696ac10cb37"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full bg-[#9a6ebd] text-white px-6 py-3 rounded-lg hover:bg-[#8a5dad] transition-colors text-center font-medium"
                  >
                    Réserver
                    <ExternalLink className="inline-block ml-2" size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className="border-b border-[#9a6ebd]/10" />

        {/* CTA Section */}
        <section className="py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#9a6ebd]/20 via-[#9a6ebd]/10 to-[#9a6ebd]/20"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTIiIGhlaWdodD0iNTIiIHZpZXdCb3g9IjAgMCA1MiA1MiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNTJ2NTJIMHoiLz48cGF0aCBkPSJNMjYgMjZtLTI1IDBhMjUgMjUgMCAxIDAgNTAgMCAyNSAyNSAwIDEgMC01MCAwIiBzdHJva2U9IiM5YTZlYmQiIHN0cm9rZS13aWR0aD0iLjUiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-[#9a6ebd]/20">
              <h2 className="text-3xl font-bold text-[#323c5f] mb-6">
                Prêt à progresser en SES ?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Réservez votre premier cours et commencez votre progression dès maintenant !
              </p>
              <a
                href="https://www.wooskill.com/fr/catalog/17929"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-[#9a6ebd] text-white px-8 py-4 rounded-lg hover:bg-[#8a5dad] transition-colors text-lg font-semibold"
              >
                <Clock className="mr-2" size={24} />
                Réserver un créneau
                <ExternalLink className="ml-2" size={20} />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CoursParticuliersPage;